import React, { useState, useMemo } from 'react';
import { Table, Tag, Button, Card, Form, Select, Input, TreeSelect, Typography, Space, Tooltip, Empty, Modal, Drawer, Popconfirm, message } from 'antd';
import { SearchOutlined, ReloadOutlined, PlusOutlined, ClockCircleOutlined, SyncOutlined, CheckCircleOutlined, MinusCircleOutlined, DeleteOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;

// ══════════════════════════════════════════════
//  常量
// ══════════════════════════════════════════════

const STATUS_MAP = {
  pending:    { label: '待处理', color: 'blue', icon: <ClockCircleOutlined /> },
  processing: { label: '处理中', color: 'orange', icon: <SyncOutlined spin /> },
  resolved:   { label: '已解决', color: 'green', icon: <CheckCircleOutlined /> },
  closed:     { label: '已关闭', color: 'default', icon: <MinusCircleOutlined /> },
};

const CATEGORY_LABEL = {
  techSupport: '技术对接',
  bug: 'BUG',
  requirement: '需求',
  other: '其他',
};

const PRIORITY_MAP = {
  low:    { label: '低',   color: 'default' },
  normal: { label: '普通', color: 'blue' },
  high:   { label: '高',   color: 'orange' },
  urgent: { label: '紧急', color: 'red' },
};

// 当前企业下所有 App（不过滤审核状态），树形二级：一级=应用名称，二级=系统版本-区域
// TODO: 联调时替换为接口数据
const MOCK_APP_TREE = [
  {
    title: '牵心Pro', value: 'app_qx',
    children: [
      { title: 'iOS-全球', value: 'app_qx-iOS-GL' },
      { title: 'Android-全球', value: 'app_qx-Android-GL' },
    ],
  },
  {
    title: 'HomeStar', value: 'app_hs',
    children: [
      { title: 'iOS-国内', value: 'app_hs-iOS-CN' },
      { title: 'Android-国内', value: 'app_hs-Android-CN' },
      { title: 'iOS-海外', value: 'app_hs-iOS-OV' },
    ],
  },
];

// 选择后展示格式：App应用名称-系统-区域
const APP_DISPLAY_MAP = {
  'app_qx-iOS-GL': '牵心Pro-iOS-全球',
  'app_qx-Android-GL': '牵心Pro-Android-全球',
  'app_hs-iOS-CN': 'HomeStar-iOS-国内',
  'app_hs-Android-CN': 'HomeStar-Android-国内',
  'app_hs-iOS-OV': 'HomeStar-iOS-海外',
};

// 当前企业项目（deleted=true 为逻辑删除；工单查询关联不过滤删除状态）
// TODO: 联调时替换为接口数据
const MOCK_PROJECTS = [
  { id: 'proj-001', name: 'SDK联调项目', createdAt: '2026-06-01 10:00', updatedAt: '2026-07-10 14:00', deleted: false },
  { id: 'proj-002', name: '私有化部署', createdAt: '2026-06-15 09:00', updatedAt: '2026-07-20 11:00', deleted: false },
  { id: 'proj-003', name: '已归档项目', createdAt: '2026-05-01 09:00', updatedAt: '2026-05-20 09:00', deleted: true },
];

// ══════════════════════════════════════════════
//  Mock 数据
//  TODO: 联调时替换为接口数据
// ══════════════════════════════════════════════

const MOCK_MY_TICKETS = [
  { key: '1', ticketNo: 'TK202607270001', title: 'test-SDK初始化接口返回错误码1001', category: 'techSupport', priority: 'urgent', status: 'pending', appValue: 'app_qx-iOS-GL', projectId: 'proj-001', projectName: 'SDK联调项目', createdAt: '2026-07-27 09:15', updatedAt: '2026-07-27 09:15' },
  { key: '2', ticketNo: 'TK202607260002', title: 'test-设备固件OTA升级失败', category: 'bug', priority: 'high', status: 'processing', appValue: 'app_hs-Android-CN', projectId: 'proj-002', projectName: '私有化部署', createdAt: '2026-07-26 14:30', updatedAt: '2026-07-27 10:00' },
  { key: '3', ticketNo: 'TK202607250003', title: 'test-App品牌配置页需求', category: 'requirement', priority: 'normal', status: 'resolved', appValue: 'app_qx-Android-GL', projectId: null, projectName: '', createdAt: '2026-07-25 11:00', updatedAt: '2026-07-26 16:00' },
  { key: '4', ticketNo: 'TK202607200004', title: 'test-云平台私有化部署', category: 'techSupport', priority: 'low', status: 'closed', appValue: '', projectId: 'proj-003', projectName: '已归档项目', createdAt: '2026-07-20 08:30', updatedAt: '2026-07-23 09:00' },
  { key: '5', ticketNo: 'TK202607270005', title: 'test-API token认证失败', category: 'techSupport', priority: 'normal', status: 'pending', appValue: 'app_hs-iOS-OV', projectId: 'proj-001', projectName: 'SDK联调项目', createdAt: '2026-07-27 10:30', updatedAt: '2026-07-27 10:30' },
];

// ══════════════════════════════════════════════
//  我的工单列表（企业视角）
// ══════════════════════════════════════════════

function TicketMyList() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [filterStatus, setFilterStatus] = useState('all');
  const [keyword, setKeyword] = useState('');
  const [filterApp, setFilterApp] = useState(undefined);
  const [filterProject, setFilterProject] = useState(undefined);
  const [tickets, setTickets] = useState(MOCK_MY_TICKETS);

  // ==================== 项目管理 ====================
  const [projects, setProjects] = useState(MOCK_PROJECTS);
  const [projectDrawerVisible, setProjectDrawerVisible] = useState(false);
  const [projectModalVisible, setProjectModalVisible] = useState(false);
  const [editingProject, setEditingProject] = useState(null); // null=新增，对象=编辑
  const [projectName, setProjectName] = useState('');

  const activeProjects = useMemo(() => projects.filter(p => !p.deleted), [projects]);

  const filteredTickets = useMemo(() => {
    let list = tickets;
    if (filterStatus !== 'all') {
      list = list.filter(t => t.status === filterStatus);
    }
    if (keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      list = list.filter(t => t.title.toLowerCase().includes(kw));
    }
    if (filterApp) {
      // 选一级（应用名）时匹配该应用下所有二级；选二级时精确匹配
      list = list.filter(t => t.appValue === filterApp || (t.appValue && t.appValue.startsWith(`${filterApp}-`)));
    }
    if (filterProject) {
      // 工单查询关联不过滤所属项目的删除状态
      list = list.filter(t => t.projectId === filterProject);
    }
    return list;
  }, [tickets, filterStatus, keyword, filterApp, filterProject]);

  const handleDelete = (record) => {
    Modal.confirm({
      title: '确认删除',
      content: '确认删除该工单？删除后不可恢复',
      okText: '确认删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: () => {
        setTickets(prev => prev.filter(t => t.key !== record.key));
        message.success('工单已删除');
      },
    });
  };

  // ==================== 项目管理：新增/编辑/逻辑删除 ====================
  const handleSaveProject = () => {
    const name = projectName.trim();
    if (!name) {
      message.warning('请输入项目名称');
      return;
    }
    const now = new Date().toISOString().replace('T', ' ').slice(0, 16);
    if (editingProject) {
      setProjects(prev => prev.map(p => p.id === editingProject.id ? { ...p, name, updatedAt: now } : p));
      message.success('项目已更新');
    } else {
      setProjects(prev => [...prev, { id: `proj-${Date.now()}`, name, createdAt: now, updatedAt: now, deleted: false }]);
      message.success('项目已创建');
    }
    setProjectModalVisible(false);
    setProjectName('');
    setEditingProject(null);
  };

  const handleDeleteProject = (record) => {
    const now = new Date().toISOString().replace('T', ' ').slice(0, 16);
    // 逻辑删除：不影响已存在的工单
    setProjects(prev => prev.map(p => p.id === record.id ? { ...p, deleted: true, updatedAt: now } : p));
    message.success('项目已删除（逻辑删除），已关联工单不受影响');
  };

  const openAddProjectModal = () => {
    setEditingProject(null);
    setProjectName('');
    setProjectModalVisible(true);
  };

  const openEditProjectModal = (record) => {
    setEditingProject(record);
    setProjectName(record.name);
    setProjectModalVisible(true);
  };

  const columns = [
    {
      title: '工单编号', dataIndex: 'ticketNo', width: 200,
      render: (text, record) => (
        <Button type="link" size="small" style={{ padding: 0, fontFamily: 'monospace' }}
          onClick={() => navigate(`/customer/ticket/my/detail/${record.ticketNo}`)}>
          {text}
        </Button>
      ),
    },
    { title: '标题', dataIndex: 'title', ellipsis: true, width: 300, render: (t) => <Tooltip title={t}>{t}</Tooltip> },
    { title: '工单分类', dataIndex: 'category', width: 100, render: (c) => CATEGORY_LABEL[c] || c },
    {
      title: '所属App', dataIndex: 'appValue', width: 170,
      render: (v) => (v ? (APP_DISPLAY_MAP[v] || v) : <span style={{ color: '#ccc' }}>-</span>),
    },
    {
      title: '所属项目', dataIndex: 'projectName', width: 130,
      render: (v) => (v ? v : <span style={{ color: '#ccc' }}>-</span>),
    },
    {
      title: '优先级', dataIndex: 'priority', width: 90,
      render: (p) => {
        const m = PRIORITY_MAP[p];
        return m ? <Tag color={m.color} style={{ border: 'none' }}>{m.label}</Tag> : p;
      },
    },
    {
      title: '状态', dataIndex: 'status', width: 100,
      render: (s) => {
        const m = STATUS_MAP[s];
        return m ? <Tag color={m.color} icon={m.icon} style={{ border: 'none' }}>{m.label}</Tag> : s;
      },
    },
    {
      title: '创建时间', dataIndex: 'createdAt', width: 180, sorter: (a, b) => a.createdAt.localeCompare(b.createdAt),
    },
    {
      title: '最近更新', dataIndex: 'updatedAt', width: 180, sorter: (a, b) => a.updatedAt.localeCompare(b.updatedAt),
    },
    {
      title: '操作', fixed: 'right', width: 160,
      render: (_, record) => (
        <Space size={4}>
          <Button type="link" size="small" onClick={() => navigate(`/customer/ticket/my/detail/${record.ticketNo}`)}>
            查看详情
          </Button>
          {record.status === 'pending' && (
            <Button type="link" size="small" danger icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}>
              删除
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={4} style={{ margin: 0 }}>我的工单</Title>
        <Space>
          <Button icon={<FolderOpenOutlined />} onClick={() => setProjectDrawerVisible(true)}>
            项目管理
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/customer/ticket/create')}>
            创建工单
          </Button>
        </Space>
      </div>

      {/* 筛选 */}
      <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Form form={form} layout="inline" initialValues={{ status: 'all' }}>
          <Form.Item name="status" style={{ marginBottom: 0 }}>
            <Select style={{ width: 140 }} onChange={setFilterStatus} placeholder="状态">
              <Option value="all">全部</Option>
              <Option value="pending">待处理</Option>
              <Option value="processing">处理中</Option>
              <Option value="resolved">已解决</Option>
              <Option value="closed">已关闭</Option>
            </Select>
          </Form.Item>
          <Form.Item name="keyword" style={{ marginBottom: 0 }}>
            <Input style={{ width: 200 }} placeholder="工单标题" allowClear
              onChange={(e) => setKeyword(e.target.value)} />
          </Form.Item>
          <Form.Item name="appValue" style={{ marginBottom: 0 }}>
            <TreeSelect
              style={{ width: 200 }}
              treeData={MOCK_APP_TREE}
              placeholder="所属App"
              allowClear
              treeDefaultExpandAll
              onChange={setFilterApp}
            />
          </Form.Item>
          <Form.Item name="projectId" style={{ marginBottom: 0 }}>
            <Select
              style={{ width: 160 }}
              placeholder="所属项目"
              allowClear
              onChange={setFilterProject}
              showSearch
              optionFilterProp="children"
            >
              {/* 查询时不过滤项目删除状态，已删除项目带标识仍可查 */}
              {projects.map(p => (
                <Option key={p.id} value={p.id}>
                  {p.name}{p.deleted ? '（已删除）' : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={() => {
                setFilterStatus(form.getFieldValue('status'));
                setKeyword(form.getFieldValue('keyword') || '');
                setFilterApp(form.getFieldValue('appValue'));
                setFilterProject(form.getFieldValue('projectId'));
              }}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={() => { form.resetFields(); setFilterStatus('all'); setKeyword(''); setFilterApp(undefined); setFilterProject(undefined); }}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 表格 */}
      <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
        <Table columns={columns} dataSource={filteredTickets} rowKey="key"
          pagination={{ showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录`, defaultPageSize: 10, pageSizeOptions: ['10', '20', '50'] }}
          locale={{
            emptyText: (
              <Empty description={
                <span>暂无工单记录，请前往 <Text strong>提交工单</Text> 发起第一个工单</span>
              } />
            ),
          }}
          scroll={{ x: 1650 }}
        />
      </Card>

      {/* ==================== 项目管理抽屉 ==================== */}
      <Drawer
        title="项目管理"
        width={560}
        open={projectDrawerVisible}
        onClose={() => setProjectDrawerVisible(false)}
      >
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAddProjectModal}>
            新增项目
          </Button>
          <Text type="secondary" style={{ marginLeft: 12, fontSize: 12 }}>删除为逻辑删除，不影响已关联工单</Text>
        </div>
        <Table
          rowKey="id"
          dataSource={activeProjects}
          pagination={false}
          size="small"
          columns={[
            { title: '项目名称', dataIndex: 'name' },
            { title: '创建时间', dataIndex: 'createdAt', width: 160 },
            { title: '更新时间', dataIndex: 'updatedAt', width: 160 },
            {
              title: '操作', width: 110,
              render: (_, record) => (
                <Space size={4}>
                  <Button type="link" size="small" onClick={() => openEditProjectModal(record)}>编辑</Button>
                  <Popconfirm
                    title="确认删除该项目？"
                    description="已关联工单不受影响"
                    okText="删除"
                    cancelText="取消"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => handleDeleteProject(record)}
                  >
                    <Button type="link" size="small" danger>删除</Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Drawer>

      {/* ==================== 新增/编辑项目弹窗 ==================== */}
      <Modal
        title={editingProject ? '编辑项目' : '新增项目'}
        open={projectModalVisible}
        onOk={handleSaveProject}
        onCancel={() => { setProjectModalVisible(false); setProjectName(''); setEditingProject(null); }}
        okText="保存"
        cancelText="取消"
      >
        <Form layout="vertical" requiredMark={false}>
          <Form.Item label="项目名称" required>
            <Input
              placeholder="请输入项目名称"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              maxLength={50}
              showCount
              onPressEnter={handleSaveProject}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default TicketMyList;
